import type { EditorPerformanceResult } from '@carboncopyco/types';
import { sumPerformanceResults } from '../../sdk/performance';

/**
 * Function used to sum all the results from given array and return average results
 */
export const getPerformanceResultAvg = (results: EditorPerformanceResult[]) => {
  if (results.length === 0) return null;

  // Sum all results using the sumPerformanceResults function
  let summedResult = results[0];
  for (let i = 1; i < results.length; i++) {
    summedResult = sumPerformanceResults(summedResult, results[i]);
  }

  // Divide each value in the summedResult by the number of results to calculate the average
  const divideObject = (obj: any, count: number): any => {
    const result: any = {};
    for (const key in obj) {
      if (typeof obj[key] === 'number') {
        result[key] = obj[key] / count; // Divide numbers by count
      } else if (typeof obj[key] === 'object') {
        result[key] = divideObject(obj[key], count); // Recursively divide objects
      }
    }
    return result;
  };

  return divideObject(summedResult, results.length);
};

export const generateCsvFile = (
  result: EditorPerformanceResult,
  shouldReloadOnStart: boolean,
  shouldStopOnLoad: boolean,
  numberOfIterations: number,
  numberOfSeconds: number
) => {
  const setup = [
    ['Setup'],
    ['Number of seconds: ', numberOfSeconds],
    ['Number of iterations: ', numberOfIterations],
    ['Reload on start: ', shouldReloadOnStart],
    ['Stop on load: ', shouldStopOnLoad],
  ];
  const nodes = [
    ['Nodes'],
    ['Div: ', result.nodes.div],
    ['Span: ', result.nodes.span],
    ['Input: ', result.nodes.input],
    ['Button: ', result.nodes.button],
    ['Link: ', result.nodes.link],
    ['Image: ', result.nodes.image],
    ['Iframe/Frame: ', result.nodes.iframe],
    ['P: ', result.nodes.p],
    ['H: ', result.nodes.h],
    ['Total nodes: ', result.nodes.total],
  ];
  const searchAndReplace = [
    ['Find and Replace'],
    ['Text: ', `${result.searchAndReplace.text.toFixed(2)}ms`],
    ['By Attribute: ', `${result.searchAndReplace.byAttribute.toFixed(2)}ms`],
    ['Input: ', `${result.searchAndReplace.input.toFixed(2)}ms`],
    [
      'Recursive on Shadow Root: ',
      `${result.searchAndReplace.recursiveOnShadowRoot.toFixed(2)}ms`,
    ],
    ['Entire Doc: ', `${result.searchAndReplace.entireDoc.toFixed(2)}ms`],
    ['Total time: ', `${result.modifications.total.toFixed(2)}ms`],
  ];

  const modifications = [
    ['Modifications'],
    ['Guidance tooltips: ', `${result.modifications.tooltip.toFixed(2)}ms`],
    [
      'Block interactions: ',
      `${result.modifications.block_interactions.toFixed(2)}ms`,
    ],
    ['Replace link: ', `${result.modifications.replace_link.toFixed(2)}ms`],
    ['Total time: ', `${result.modifications.total.toFixed(2)}ms`],
  ];
  const rules = [
    ['Rules'],
    ['Text: ', `${result.rules.text.toFixed(2)}ms`],
    ['Input: ', `${result.rules.input.toFixed(2)}ms`],
    ['List: ', `${result.rules.list.toFixed(2)}ms`],
    ['Link: ', `${result.rules.link.toFixed(2)}ms`],
    ['Image: ', `${result.rules.image.toFixed(2)}ms`],
    ['Lock: ', `${result.rules.lock.toFixed(2)}ms`],
    ['Unlock: ', `${result.rules.unlock.toFixed(2)}ms`],
    ['Visibility: ', `${result.rules.visibility.toFixed(2)}ms`],
    ['Style: ', `${result.rules.style.toFixed(2)}ms`],
    ['Delete: ', `${result.rules.delete.toFixed(2)}ms`],
    ['Chart: ', `${result.rules.chart.toFixed(2)}ms`],
    ['Total: ', `${result.rules.total.toFixed(2)}ms`],
  ];
  const mutations = [
    ['Mutations'],
    ['Callback called: ', `${result.mutations.callbackCalled}`],
    ['Callback time: ', `${result.mutations.callbackTime.toFixed(2)}ms`],
    ['Total number: ', `${result.mutations.total}`],
  ];

  const cache = [
    ['Cache'],
    [
      'Condense whitespace - items added: ',
      `${result.cache.condenseWhiteSpace}`,
    ],
    [
      'Condense whitespace - cache cleared: ',
      `${result.cache.condenseWhiteSpaceClear}`,
    ],
    [
      'Text content selector resolver - items added: ',
      `${result.cache.textContentSelectorResolver}`,
    ],
    [
      'Text content selector resolver - cache cleared: ',
      `${result.cache.textContentSelectorResolverClear}`,
    ],
  ];

  const csvContent = `${setup.map((e) => e.join(',')).join('\n')}\n\n${nodes.map((e) => e.join(',')).join('\n')}\n\n${searchAndReplace.map((e) => e.join(',')).join('\n')}\n\n${rules.map((e) => e.join(',')).join('\n')}\n\n${modifications.map((e) => e.join(',')).join('\n')}\n\n${mutations.map((e) => e.join(',')).join('\n')}\n\n${cache.map((e) => e.join(',')).join('\n')}\n\n`;

  return csvContent;
};
